import * as React from "react";

import cx from "classnames";

import { ValidationError } from "~/ui";

import { ReactComponent as TickIcon } from "~/assets/svg/tick.svg";

import styled from "styled-components";
import { rgba } from "polished";
import { SectionModuleProps, useModuleRefs } from "./ModuleSection";

import _store from "store2";
import { useGlobalUser } from "~/state";
import { theme } from "~/theme";

interface Question {
  question: string;
  options: React.ReactNode[];
  answer: number[];
  related: HTMLElement | null;
}

interface QuizFormProps {
  quizKey: number;
  data: Question[];
  showAnswers?: boolean;
  onSuccess: () => void;
  onFail: () => void;
  onResetClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNextClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  nextText?: React.ReactNode;
  quizHeading: string;
}

interface QuizFormState {
  screen: "quiz" | "results";
  currentQuestion: number;
  showError: boolean;
}

const DEFAULT_STATE: QuizFormState = {
  screen: "quiz",
  currentQuestion: 0,
  showError: false,
};

export const ModuleQuizForm: React.FC<QuizFormProps> = ({
  quizKey,
  data,
  showAnswers,
  onSuccess,
  onFail,
  onNextClick,
  onResetClick,
  nextText,
  quizHeading,
}) => {
  const [userState, setUserState] = useGlobalUser();

  const CORRECT_ANSWERS = React.useMemo(() => {
    return data.reduce((acc, cur, idx) => {
      acc[idx] = cur.answer;
      return acc;
    }, {} as Record<number, number[]>);
  }, [data]);

  const [state, setState] = React.useState<QuizFormState>(() => {
    if (userState.usersModuleAnswers[quizKey]) {
      const savedData = userState.usersModuleAnswers[quizKey];
      return {
        ...DEFAULT_STATE,
        userAnswers: savedData,
      };
    }

    return DEFAULT_STATE;
  });

  const resetQuiz = () => {
    setState(DEFAULT_STATE);
    setUserState((prev) => ({
      ...prev,
      usersModuleAnswers: {
        ...prev.usersModuleAnswers,
        [quizKey]: {},
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const $userAnswers = userState.usersModuleAnswers[quizKey] || {};
    const $currentQuestion = state.currentQuestion;

    if (
      !$userAnswers[$currentQuestion] ||
      $userAnswers[$currentQuestion].length === 0
    ) {
      setState((prev) => ({
        ...prev,
        showError: true,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      showError: false,
      screen: "results",
    }));
  };

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const $userAnswers = userState.usersModuleAnswers[quizKey] || {};
    const $currentQuestion = state.currentQuestion;

    if (
      !$userAnswers[$currentQuestion] ||
      $userAnswers[$currentQuestion].length === 0
    ) {
      setState((prev) => ({
        ...prev,
        showError: true,
      }));
      return;
    }

    setState((prev) => {
      const nextState = {
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        showError: false,
      };

      return nextState;
    });

    setUserState((prev) => ({
      ...prev,
      usersModuleAnswers: {
        ...prev.usersModuleAnswers,
        [quizKey]: $userAnswers,
      },
    }));
  };

  const createCheckboxChangeHandler = (
    questionIdx: number,
    optionIdx: number,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserState((prev) => {
      const selectedAnswers: number[] =
        (prev.usersModuleAnswers[quizKey] &&
          prev.usersModuleAnswers[quizKey][questionIdx]) ||
        [];
      const uniqueAnswers = new Set<number>(selectedAnswers || []);

      if (uniqueAnswers.has(optionIdx)) {
        uniqueAnswers.delete(optionIdx);
      } else {
        uniqueAnswers.add(optionIdx);
      }

      const nextState: typeof prev = {
        ...prev,
        usersModuleAnswers: {
          ...prev.usersModuleAnswers,
          [quizKey]: {
            ...prev.usersModuleAnswers[quizKey],
            [questionIdx]: Array.from(uniqueAnswers),
          },
        },
      };

      return nextState;
    });
  };

  const isChecked = (questionIdx: number, optionIdx: number) => {
    if (
      !userState.usersModuleAnswers[quizKey] ||
      !userState.usersModuleAnswers[quizKey][questionIdx]
    ) {
      return false;
    }

    if (
      !userState.usersModuleAnswers[quizKey][questionIdx].includes(optionIdx)
    ) {
      return false;
    }

    return true;
  };

  if (state.screen === "quiz") {
    return (
      <div className="card">
        <div className="card-body bg-light">
          <form onSubmit={handleSubmit}>
            <h2 className="text-center text-warning mb-5">{quizHeading}</h2>

            {data.map((q, qi) => (
              <fieldset
                key={qi}
                className={cx(
                  "mb-4",
                  qi === state.currentQuestion ? "" : "d-none invisible",
                )}
              >
                <legend className="font-weight-bold mb-3">
                  <span className="text-warning">Q{qi + 1}.</span>{" "}
                  <span className="text-info">{q.question}</span>
                </legend>
                <QuestionList>
                  {q.options.map((o, oi) => {
                    const id = `q${qi}-${oi}`;

                    return (
                      <li key={oi}>
                        <input
                          type="checkbox"
                          id={id}
                          name={`${qi}`}
                          value={0}
                          onChange={createCheckboxChangeHandler(qi, oi)}
                          checked={isChecked(qi, oi)}
                        />
                        <label
                          style={
                            showAnswers && CORRECT_ANSWERS[qi].includes(oi)
                              ? {
                                  boxShadow: "0 0 0 3px green",
                                }
                              : undefined
                          }
                          htmlFor={id}
                        >
                          <span>{o}</span>
                        </label>
                      </li>
                    );
                  })}
                </QuestionList>
              </fieldset>
            ))}

            {state.showError && (
              <ValidationError>
                <span className="d-block mb-3">Please choose one or more answers!</span>
              </ValidationError>
            )}

            <div className="text-center">
              {state.currentQuestion === data.length - 1 ? (
                <button className="btn btn-secondary" type="submit">
                  Submit answers
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="btn btn-secondary"
                  type="button"
                >
                  Next question
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <QuizResults
      data={data}
      userAnswers={userState.usersModuleAnswers[quizKey]}
      answers={CORRECT_ANSWERS}
      onSuccess={onSuccess}
      onNextClick={onNextClick}
      onFail={onFail}
      onResetClick={(e) => {
        e.preventDefault();
        resetQuiz();
        onResetClick && onResetClick(e);
      }}
      nextText={nextText}
    />
  );
};

const sortNumerical = (a: number, b: number) => a - b;

interface QuizResultsProps {
  data: Question[];
  userAnswers: Record<number, number[]>;
  answers: Record<number, number[]>;
  onSuccess: () => void;
  onFail?: () => void;
  onResetClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNextClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  nextText?: React.ReactNode;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  data,
  userAnswers,
  answers,
  onSuccess,
  onFail,
  onResetClick,
  onNextClick,
  nextText,
}) => {
  const { refList } = useModuleRefs();

  const $answers = React.useMemo(() => {
    return Object.entries(answers);
  }, [answers]);

  const $userAnswers = React.useMemo(() => {
    return Object.entries(userAnswers);
  }, [userAnswers]);

  const results = React.useMemo(() => {
    const questionResults = $answers.map((a, idx) => {
      const setA = Array.from(new Set(a[1])).sort(sortNumerical);
      const setB = Array.from(new Set($userAnswers[idx][1])).sort(
        sortNumerical,
      );

      return JSON.stringify(setA) === JSON.stringify(setB);
    });

    const correctCount = questionResults.filter((x) => x === true).length;

    return {
      questionResults,
      correctCount,
    };
  }, [$answers, $userAnswers]);

  const incorrectQuestions = results.questionResults
    .map((v, i) => {
      if (v === false) {
        return i + 1;
      }

      return null;
    })
    .filter((x) => x !== null);

  const reviewRefs = React.useMemo(() => {
    const _ = data
      .filter((x, i) => results.questionResults[i] === false)
      .reduce((acc, cur) => {
        acc.set(cur.related!, refList.current.get(cur.related!)!);
        return acc;
      }, new Map<HTMLElement, SectionModuleProps>());

    return Array.from(_.entries()).map(([k, v]) => {
      return {
        href: `#${k.id}`,
        children: v.sectionTitle,
      };
    });
  }, [data, results.questionResults]);

  React.useEffect(() => {
    if (results.correctCount === $answers.length) {
      onSuccess();
      return;
    }

    onFail && onFail();
  }, []);

  if (results.correctCount === $answers.length) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <TickIcon
            width={80}
            height={80}
            className="mb-3"
            css={`
              fill: ${theme.gold};
            `}
          />
          <h4 className="h2 card-title text-sage">
            Congratulations! You got all the answers right.
          </h4>
        </div>
        <div className="card-footer bg-sage">
          <h5 className="text-center mb-0">
            <button
              className="btn btn-link text-white text-uppercase"
              type="button"
              onClick={onNextClick}
            >
              {nextText || "Continue to next module"}
            </button>
          </h5>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-body">
        <h4 className="h2 card-title text-center text-warning">
          You scored {results.correctCount} out of {$answers.length}
        </h4>
        <div className="d-flex flex-column text-center">
          {incorrectQuestions.length === 1 ? (
            <p className="card-text lead text-info mb-3">
              You need to correct your answer to question{" "}
              {incorrectQuestions[0]} to proceed
            </p>
          ) : (
            <p className="card-text lead text-info mb-3">
              You need to correct your answers to questions{" "}
              {incorrectQuestions.slice(0, -1).join(", ")} and{" "}
              {incorrectQuestions.slice(-1)} to proceed
            </p>
          )}
          {!!reviewRefs.length && (
            <>
              <hr className="w-100" />
              <strong className="card-text mb-3">
                Please review the following sections and try the quiz again:
              </strong>
              <ul className="list-unstyled">
                {reviewRefs.map((x, idx) => (
                  <li key={idx}>
                    <a href={x.href}>{x.children}</a>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
      <div className="card-footer text-center bg-primary">
        <button
          className="h5 text-white text-uppercase mb-0 btn btn-link"
          onClick={onResetClick}
          type="button"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

const QuestionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  counter-reset: listCount;

  > li:first-child > label {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }

  > li:last-child > label {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  > li > label {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    padding: 1rem;
    border: 1px solid ${(p) => p.theme.gray300};
    position: relative;
    margin-top: -1px;
    background: #fff;
    cursor: pointer;

    &:before {
      content: counter(listCount, upper-alpha);
      counter-increment: listCount;
      display: flex;
      flex: 0 0 2rem;
      margin-right: 1rem;
      height: 2rem;
      border-radius: 50%;
      background: ${(p) => p.theme.secondary};
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 1.25rem;
      color: ${(p) => p.theme.info};
    }
  }

  input {
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;

    &:focus ~ label {
      z-index: 1;
      box-shadow: 0 0 0 0.2rem ${(p) => rgba(p.theme.warning, 0.5)};
    }
    &:checked ~ label {
      background: ${(p) => rgba(p.theme.secondary, 0.2)};
    }
  }
`;
